import React, { useState, useEffect, useCallback } from 'react';
import { Star, ThumbsUp, Camera, X, CheckCircle, Shield, ChevronDown, Send, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../context/SettingsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StarRating = ({ rating, onRate, interactive = false, size = 'w-5 h-5' }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        disabled={!interactive}
        onClick={() => interactive && onRate?.(star)}
        className={interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'}
      >
        <Star className={`${size} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
      </button>
    ))}
  </div>
);

const ReviewCard = ({ review, onHelpful }) => {
  const settings = useSettings();
  const [expanded, setExpanded] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  return (
    <>
      <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl" data-testid={`review-${review.id}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
              <span className="text-sm font-bold text-teal-600">{(review.customer_name || 'A')[0].toUpperCase()}</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-800 dark:text-white text-sm">{review.customer_name}</span>
                {review.is_verified_purchase && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full" data-testid="verified-badge">
                    <CheckCircle className="w-3 h-3" /> Verified Purchase
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <StarRating rating={review.rating} size="w-3.5 h-3.5" />
                <span className="text-xs text-gray-400">
                  {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {review.title && (
          <h4 className="font-semibold text-gray-800 dark:text-white text-sm mt-3">{review.title}</h4>
        )}

        <p className={`text-sm text-gray-600 dark:text-gray-300 mt-2 ${!expanded && review.comment?.length > 200 ? 'line-clamp-3' : ''}`}>
          {review.comment}
        </p>
        {review.comment?.length > 200 && (
          <button onClick={() => setExpanded(!expanded)} className="text-xs text-teal-600 mt-1 hover:underline">
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}

        {/* Review Images */}
        {review.images?.length > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {review.images.map((img, i) => (
              <button key={i} onClick={() => setLightboxImg(img)} className="w-16 h-16 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-teal-400 transition-colors">
                <img src={img} alt={`Review ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Admin Reply */}
        {review.admin_reply && (
          <div className="mt-3 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border-l-3 border-teal-500">
            <p className="text-xs font-medium text-teal-700 dark:text-teal-400 mb-1 flex items-center gap-1">
              <Shield className="w-3 h-3" /> {settings.site_name || 'Store'} Response
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300">{review.admin_reply}</p>
          </div>
        )}

        {/* Helpful */}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t dark:border-gray-600">
          <button
            onClick={() => onHelpful(review.id)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="helpful-btn"
          >
            <ThumbsUp className="w-3.5 h-3.5" />
            Helpful ({review.helpful_count || 0})
          </button>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxImg && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightboxImg(null)}>
          <button className="absolute top-4 right-4 text-white" onClick={() => setLightboxImg(null)}>
            <X className="w-8 h-8" />
          </button>
          <img src={lightboxImg} alt="Review" className="max-w-full max-h-[80vh] rounded-lg" />
        </div>
      )}
    </>
  );
};

const ReviewForm = ({ productId, userId, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleImageAdd = () => {
    const url = prompt('Enter image URL:');
    if (url && url.startsWith('http')) {
      setImages(prev => [...prev, url]);
    }
  };

  const handleRemoveImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) { toast.error('Please select a rating'); return; }
    if (!comment.trim()) { toast.error('Please write a review'); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('medicareToken');
      const res = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          product_id: productId,
          customer_id: userId,
          rating,
          title,
          comment,
          images,
        }),
      });

      if (res.ok) {
        toast.success('Review submitted! It will appear after approval.');
        setRating(0); setTitle(''); setComment(''); setImages([]);
        onSubmit?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit review');
      }
    } catch (err) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="review-form">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Rating</label>
        <StarRating rating={rating} onRate={setRating} interactive size="w-7 h-7" />
      </div>
      <div>
        <input
          type="text"
          placeholder="Review title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-4 py-2.5 border dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-teal-500 text-sm"
          data-testid="review-title-input"
        />
      </div>
      <div>
        <textarea
          placeholder="Share your experience with this product..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          className="w-full px-4 py-2.5 border dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-teal-500 text-sm"
          data-testid="review-comment-input"
        />
      </div>

      {/* Image Upload */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <button type="button" onClick={handleImageAdd} className="flex items-center gap-1 px-3 py-1.5 border dark:border-gray-600 rounded-lg text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-testid="add-review-image-btn">
            <Camera className="w-3.5 h-3.5" /> Add Photo
          </button>
          <span className="text-xs text-gray-400">{images.length}/5 photos</span>
        </div>
        {images.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {images.map((img, i) => (
              <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden">
                <img src={img} alt={`Upload ${i + 1}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => handleRemoveImage(i)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 font-medium transition-colors disabled:opacity-50"
        data-testid="submit-review-btn"
      >
        {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Send className="w-4 h-4" />}
        Submit Review
      </button>
    </form>
  );
};

const ProductReviews = ({ productId, userId, showForm = false }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [sortBy, setSortBy] = useState('recent');
  const [filterRating, setFilterRating] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      let url = `${API_URL}/api/reviews?product_id=${productId}&is_approved=true`;
      const res = await fetch(url);
      if (res.ok) setReviews(await res.json());
    } catch (err) { console.error(err); }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      setLoading(true);
      fetchReviews().finally(() => setLoading(false));
    }
  }, [productId, fetchReviews]);

  const handleHelpful = async (reviewId) => {
    try {
      const res = await fetch(`${API_URL}/api/reviews/${reviewId}/helpful`, { method: 'POST' });
      if (res.ok) {
        setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r));
        toast.success('Thanks for your feedback!');
      }
    } catch (err) { console.error(err); }
  };

  const filteredReviews = reviews
    .filter(r => !filterRating || r.rating === filterRating)
    .sort((a, b) => {
      if (sortBy === 'helpful') return (b.helpful_count || 0) - (a.helpful_count || 0);
      if (sortBy === 'highest') return b.rating - a.rating;
      if (sortBy === 'lowest') return a.rating - b.rating;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const avgRating = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;
  const ratingDist = [5, 4, 3, 2, 1].map(r => ({ rating: r, count: reviews.filter(rev => rev.rating === r).length }));

  return (
    <div className="space-y-6" data-testid="product-reviews">
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="text-center md:text-left">
          <div className="text-5xl font-bold text-gray-800 dark:text-white">{avgRating}</div>
          <StarRating rating={Math.round(avgRating)} size="w-5 h-5" />
          <p className="text-sm text-gray-500 mt-1">{reviews.length} reviews</p>
        </div>
        <div className="flex-1 space-y-1.5">
          {ratingDist.map(({ rating, count }) => (
            <button key={rating} onClick={() => setFilterRating(filterRating === rating ? null : rating)} className="flex items-center gap-2 w-full group">
              <span className="text-xs text-gray-500 w-4">{rating}</span>
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                <div className="h-full bg-yellow-400 rounded-full transition-all" style={{ width: `${reviews.length ? (count / reviews.length) * 100 : 0}%` }} />
              </div>
              <span className="text-xs text-gray-400 w-8">{count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        {userId && showForm && (
          <button
            onClick={() => setShowReviewForm(!showReviewForm)}
            className="px-4 py-2 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 transition-colors"
            data-testid="write-review-btn"
          >
            Write a Review
          </button>
        )}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 border dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
          data-testid="review-sort"
        >
          <option value="recent">Most Recent</option>
          <option value="helpful">Most Helpful</option>
          <option value="highest">Highest Rating</option>
          <option value="lowest">Lowest Rating</option>
        </select>
        {filterRating && (
          <button onClick={() => setFilterRating(null)} className="px-3 py-1.5 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
            {filterRating} Stars &times;
          </button>
        )}
      </div>

      {/* Review Form */}
      {showReviewForm && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border-2 border-teal-200 dark:border-teal-800">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">Write Your Review</h3>
          <ReviewForm productId={productId} userId={userId} onSubmit={() => { setShowReviewForm(false); fetchReviews(); }} />
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredReviews.length === 0 ? (
        <div className="text-center py-8">
          <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No reviews yet. Be the first to review!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredReviews.map((review) => (
            <ReviewCard key={review.id} review={review} onHelpful={handleHelpful} />
          ))}
        </div>
      )}
    </div>
  );
};

export { ReviewForm, ReviewCard, StarRating };
export default ProductReviews;
